import { useParams, Link } from "react-router-dom";
import { Phone, Clock, Shield, CheckCircle, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingCTA from "../components/FloatingCTA";
import ContactForm from "../components/ContactForm";
import { services, getServiceBySlug, generateServiceContent } from "../data/services";

export const ServicePage = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return (
      <>
        <Header />
        <main className="section-padding bg-white">
          <div className="container-custom text-center">
            <h1 className="text-4xl font-bold mb-4">Service non trouvé</h1>
            <p className="text-zinc-600 mb-8">Le service que vous recherchez n'existe pas.</p>
            <Link to="/">
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const content = generateServiceContent(service);

  return (
    <>
      <SEOHead
        title={content.title}
        description={content.metaDescription}
        type="Service"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 bg-zinc-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={service.image}
              alt={service.imageAlt}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/95 to-zinc-950/80" />
          </div>

          <div className="container-custom relative z-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-zinc-400 mb-8">
              <Link to="/" className="hover:text-yellow-400 transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/#services" className="hover:text-yellow-400 transition-colors">Services</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-yellow-400">{service.name}</span>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-6 font-heading" data-testid="service-page-title">
                {content.h1}
              </h1>
              <p className="text-lg text-zinc-300 mb-8">
                {content.intro}
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-sm px-4 py-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="text-white text-sm">Intervention 15-30 min</span>
                </div>
                <div className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-sm px-4 py-2">
                  <Shield className="w-5 h-5 text-yellow-400" />
                  <span className="text-white text-sm">Devis avant intervention</span>
                </div>
              </div>

              {/* CTA */}
              <a href="tel:0668032561" data-testid="service-call-button">
                <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-sm animate-pulse-glow">
                  <Phone className="w-5 h-5 mr-3" />
                  Appeler – 06 68 03 25 61
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Content Sections */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-12">
                {content.sections.map((section, index) => (
                  <div key={index} className="prose prose-lg max-w-none">
                    <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-4 font-heading">
                      {section.h2}
                    </h2>
                    {section.h3 && (
                      <ul className="list-disc pl-6 mb-4">
                        {section.h3.map((item, i) => (
                          <li key={i} className="text-zinc-600">{item}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-zinc-600 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}

                {/* CTA Box */}
                <div className="bg-zinc-950 rounded-sm p-8 text-center">
                  <h3 className="text-2xl font-bold uppercase text-white mb-4 font-heading">
                    Besoin de ce service ?
                  </h3>
                  <p className="text-zinc-300 mb-6">
                    Notre serrurier intervient rapidement à Lieusaint et dans toute la zone de Sénart.
                  </p>
                  <a href="tel:0668032561">
                    <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-8 py-4">
                      <Phone className="w-5 h-5 mr-2" />
                      06 68 03 25 61
                    </Button>
                  </a>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Other Services */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6">
                  <h3 className="text-xl font-bold uppercase tracking-tight text-zinc-900 mb-6 font-heading">
                    Autres services
                  </h3>
                  <div className="space-y-3">
                    {services.filter(s => s.slug !== slug).map((s) => (
                      <Link
                        key={s.id}
                        to={`/service/${s.slug}`}
                        className="flex items-center gap-3 p-3 bg-white border border-zinc-200 rounded-sm hover:border-yellow-400 hover:bg-yellow-50 transition-all group"
                      >
                        <CheckCircle className="w-5 h-5 text-yellow-500" />
                        <span className="text-zinc-700 group-hover:text-zinc-900 font-medium text-sm flex-1">
                          {s.name}
                        </span>
                        <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-yellow-500" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Contact Card */}
                <div className="bg-yellow-400 rounded-sm p-6 text-center">
                  <h3 className="text-xl font-bold uppercase tracking-tight text-black mb-4 font-heading">
                    Urgence ?
                  </h3>
                  <p className="text-black/80 mb-4">
                    Intervention rapide 24h/24
                  </p>
                  <a href="tel:0668032561" className="block">
                    <div className="bg-black text-white font-bold text-xl py-4 rounded-sm hover:bg-zinc-800 transition-colors">
                      06 68 03 25 61
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ContactForm />
      </main>
      <Footer />
      <FloatingCTA />
    </>
  );
};

export default ServicePage;
